import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useInquiry } from "@/contexts/InquiryContext";

const links = [
  { label: "Home", href: "/#home", type: "hash" },
  { label: "Apartments", href: "/#apartments", type: "hash" },
  { label: "Consultancy", href: "/consultancy", type: "route" },
  { label: "About", href: "/#about", type: "hash" },
  { label: "Contact", href: "/#contact", type: "hash" },
];

export default function Navbar({ variant = "light" }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { openInquiry } = useInquiry();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = location.pathname === "/";
  const isDarkVariant = variant === "dark";
  const transparentTop = (isHome || isDarkVariant) && !scrolled;

  const solidBg = isDarkVariant
    ? "bg-[#1F2620]/90 backdrop-blur-md border-b border-[#EDE4D3]/10"
    : "bg-[#F7F3EA]/90 backdrop-blur-md border-b border-[#E5E0D5]";

  const brandColor = transparentTop
    ? "text-white"
    : isDarkVariant
      ? "text-[#F7F3EA]"
      : "text-[#2B3324]";
  const brandAccent = isDarkVariant ? "text-[#B8865B]" : "text-[#C5A059]";

  const linkColor = transparentTop
    ? "text-white/90 hover:text-[#C5A059]"
    : isDarkVariant
      ? "text-[#EDE4D3]/85 hover:text-[#B8865B]"
      : "text-[#2B3324] hover:text-[#6E7F5B]";

  const ctaBg = isDarkVariant
    ? "bg-[#B8865B] hover:bg-[#A57548] text-[#1F2620]"
    : "bg-[#6E7F5B] hover:bg-[#5C6B4D] text-white";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparentTop ? "bg-transparent" : solidBg
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className={`font-display tracking-tight text-xl md:text-2xl font-medium transition-colors ${brandColor}`}
        >
          Kenneth Nathanael <span className={brandAccent}>Lukman</span>
          <span className={`ml-1 text-xs align-top ${transparentTop ? "text-white/70" : isDarkVariant ? "text-[#EDE4D3]/50" : "text-[#5C6B4D]"}`}>
            LLC
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.label}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`text-sm tracking-wide font-medium transition-colors ${linkColor}`}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`text-sm tracking-wide font-medium transition-colors ${linkColor}`}
              >
                {l.label}
              </a>
            ),
          )}
          <button
            type="button"
            onClick={() => openInquiry()}
            data-testid="nav-cta"
            className={`px-5 py-2.5 rounded-md text-sm font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-sm ${ctaBg}`}
          >
            Inquire Now
          </button>
        </nav>

        <button
          onClick={() => setOpen((o) => !o)}
          className="md:hidden p-2"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? (
            <X className={transparentTop ? "text-white" : isDarkVariant ? "text-[#F7F3EA]" : "text-[#2B3324]"} />
          ) : (
            <Menu className={transparentTop ? "text-white" : isDarkVariant ? "text-[#F7F3EA]" : "text-[#2B3324]"} />
          )}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className={`md:hidden overflow-hidden ${isDarkVariant ? "bg-[#1F2620] border-t border-[#EDE4D3]/10" : "bg-[#F7F3EA] border-t border-[#E5E0D5]"}`}
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) =>
                l.type === "route" ? (
                  <Link
                    key={l.label}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className={`text-base font-medium ${isDarkVariant ? "text-[#EDE4D3]" : "text-[#2B3324]"}`}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.label}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className={`text-base font-medium ${isDarkVariant ? "text-[#EDE4D3]" : "text-[#2B3324]"}`}
                  >
                    {l.label}
                  </a>
                ),
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  openInquiry();
                }}
                data-testid="nav-mobile-cta"
                className={`px-5 py-3 rounded-md text-sm font-medium text-center ${ctaBg}`}
              >
                Inquire Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
